export interface BadgeBounds { x: number; y: number; width: number; height: number }

/**
 * Where a "−N" loss label sits: its text-baseline centre. Shared by live losses
 * and frozen log snapshots; fontSize is the loss label size.
 *
 * By default the attacker's label goes up-left of its troop anchor and the
 * defender's up-right. With `bounds` (the drawn map) the label never leaves the
 * board: a label that would run off the right edge goes on the left instead
 * (and vice versa), and one that would clip at the top drops below the anchor.
 */
export function attackBadgePosition(
  cx: number,
  cy: number,
  side: -1 | 1,
  fontSize: number,
  opts: { label?: string; bounds?: BadgeBounds } = {},
) {
  const offset = fontSize * 0.6
  let x = cx + side * offset
  let y = cy - fontSize
  const b = opts.bounds
  if (!b) return { x, y }

  // Bold digits run ~0.62em wide; the black outline adds ~0.45em overall.
  const halfWidth = (((opts.label?.length ?? 2) * 0.62 + 0.45) * fontSize) / 2
  const ascent = fontSize * 0.95 // cap height plus outline above the baseline
  const descent = fontSize * 0.25
  const left = b.x
  const right = b.x + b.width

  if (x + halfWidth > right) x = cx - offset
  else if (x - halfWidth < left) x = cx + offset
  x = Math.min(Math.max(x, left + halfWidth), right - halfWidth)

  if (y - ascent < b.y) y = cy + fontSize * 1.6
  y = Math.min(y, b.y + b.height - descent)
  return { x, y }
}

/**
 * Which side of its troop anchor each loss label goes on: away from the other
 * territory, so the pair never converge on the border between them. A fixed
 * attacker-left / defender-right split stacked one label on the other whenever
 * the attacker was the eastern territory and the two sat close together.
 */
export function attackBadgeSides(
  attacker: { x: number } | undefined,
  defender: { x: number } | undefined,
): { attacker: -1 | 1; defender: -1 | 1 } {
  const attackerEast = !!attacker && !!defender && attacker.x > defender.x
  return attackerEast ? { attacker: 1, defender: -1 } : { attacker: -1, defender: 1 }
}
